import { getAugmentedSearchQuery, getUseGeoLocation } from './search';
import { getAuthToken, getZipCodeGeoCoordinates } from './user';
import {
    LOAD_DISCOVER_ITEMS_FAIL,
    LOAD_DISCOVER_ITEMS_REQUEST,
    LOAD_DISCOVER_ITEMS_SUCCESS,
    LOAD_SHOULD_DISCOVER_SUCCESS,
} from './actions';
import api from '@/redux/api/search';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from '@reduxjs/toolkit';
import discoverTopicApi from '../api/discover';
import { getDeployment } from './config';
import { getUserExcludedFacets } from './searchExclusions';
import { GlobalState } from '../rootReducer';
import isEqual from 'lodash/isEqual';
import ms from 'ms';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
export const DEFAULT_STATE = {
    itemIds: [],
    loaded: 0,
    loading: false,
    searchQuery: {},
    sortOptions: [],
    topics: {},
    totalPages: 0,
    totalRecords: 0,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_DISCOVER_ITEMS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_DISCOVER_ITEMS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_DISCOVER_ITEMS_SUCCESS:
            return {
                ...state,
                itemIds: cloneDeep(action.payload.itemIDs),
                loaded: action.meta.actionTime,
                loading: false,
                searchQuery: action.meta.searchQuery,
                sortOptions: action.payload.sort,
                totalPages: action.payload.totalPages,
                totalRecords: action.payload.totalFound,
            };
        case LOAD_SHOULD_DISCOVER_SUCCESS:
            const topics = { ...state.topics, [action.meta.topic]: action.payload };
            return {
                ...state,
                topics,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.discover;
const idSelector = (state: GlobalState, id) => id;

const topicsSelector = createSelector(stateSelector, (state) => state.topics);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getShouldDiscoverTopic: (state: any, selector: any) => any = createSelector(
    [topicsSelector, idSelector],
    (topics, id) => topics[id] || false
);

export const getDiscoverPaginationFilter = createSelector(stateSelector, (state) => ({
    totalPages: state.totalPages,
    totalRecords: state.totalRecords,
}));

export const getDiscoverItemIds = createSelector(stateSelector, (state) => state.itemIds || []);

const getLoadTimeForDiscoverItemIds = createSelector([loadedSelector], (loaded) => loaded || 0);

const isDiscoverItemIdsLoading = createSelector([loadingSelector], (loading) => loading);

const shouldFetchDiscoverItemIds = (state: GlobalState, searchQuery: any) => {
    const data = stateSelector(state);
    if (data && data.totalPages) {
        if (!isEqual(data.searchQuery, searchQuery)) {
            return true;
        }
        const loaded = getLoadTimeForDiscoverItemIds(state);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isDiscoverItemIdsLoading(state);
    return !loading;
};

/* ACTION CREATORS */

const loadDiscoverItemIds = (searchQuery: any) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        dispatch({
            type: LOAD_DISCOVER_ITEMS_REQUEST,
        });

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedSearchQuery = getAugmentedSearchQuery(searchQuery, searchExclusions);

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        const results = await api.fetchSearchResults({
            authToken,
            deployment,
            geoCoordinates,
            searchQuery: augmentedSearchQuery,
            useGeoLocation,
        });

        return dispatch({
            meta: { actionTime: Date.now(), searchQuery },
            payload: results.payload,
            type: LOAD_DISCOVER_ITEMS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_DISCOVER_ITEMS_FAIL,
        });
    }
};

const fetchShouldDiscoverTopic = (topic: string) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const results = await discoverTopicApi.fetchShouldDiscoverTopic({
            authToken,
            deployment,
            topic,
        });

        return dispatch({
            meta: { actionTime: Date.now(), topic },
            payload: results.payload,
            type: LOAD_SHOULD_DISCOVER_SUCCESS,
        });
    } catch (e) {
        console.log('Error loading discover topic', e);
    }
};

export const fetchShouldDiscoverTopicIfNeeded = (topic: string) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (!getShouldDiscoverTopic(state, topic)) {
        return dispatch(fetchShouldDiscoverTopic(topic));
    }
    return Promise.resolve();
};

export const fetchDiscoverItemIdsIfNeeded = (searchQuery) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchDiscoverItemIds(state, searchQuery)) {
        return dispatch(loadDiscoverItemIds(searchQuery));
    }
    return Promise.resolve();
};
